import { create } from 'zustand';
import type { StringWithAutocomplete } from '@eliadmin/core/src/types/misc-types';

export type SupportedLocale = {
  locale: string;
  name: string;
  is_default?: boolean;
  image?: {
    id: number;
    name: string;
    url: string;
    custom_title: string;
  };
};

type CommonLocale =
  | 'sk'
  | 'cs'
  | 'en'
  | 'de'
  | 'hu'
  | 'pl'
  | 'it'
  | 'si'
  | 'hr'
  | 'fr'
  | 'ru'
  | 'ro'
  | 'es'
  | 'fi'
  | 'se';

export type AvailableLocales = StringWithAutocomplete<CommonLocale>;

export type LocaleConfig = {
  supportedLocales: SupportedLocale[];
  currentLocale: AvailableLocales;
  defaultLocale?: AvailableLocales;
};

export const useLocaleStore = create<LocaleConfig>(() => ({
  supportedLocales: [
    {
      locale: 'sk',
      name: 'Slovenčina',
    },
    {
      locale: 'cs',
      name: 'Čeština',
    },
  ] as SupportedLocale[],
  currentLocale: 'sk',
  defaultLocale: 'sk',
}));

export function normalizeLocaleConfig(state: LocaleConfig): LocaleConfig {
  const supportedLocaleCodes = state.supportedLocales.map(locale => locale.locale).filter(Boolean);

  if (!supportedLocaleCodes.length) return state;

  const supportedLocales = new Set(supportedLocaleCodes);
  const fallbackLocale = (
    state.defaultLocale && supportedLocales.has(state.defaultLocale)
      ? state.defaultLocale
      : supportedLocaleCodes[0]
  ) as AvailableLocales;
  const currentLocale = supportedLocales.has(state.currentLocale)
    ? state.currentLocale
    : fallbackLocale;

  if (currentLocale === state.currentLocale && fallbackLocale === state.defaultLocale) {
    return state;
  }

  return {
    ...state,
    currentLocale,
    defaultLocale: fallbackLocale,
  };
}

export function initLocaleStore(state: LocaleConfig) {
  useLocaleStore.setState(normalizeLocaleConfig(state));
}

export function hydrateLocaleStore(state: LocaleConfig) {
  Object.assign(useLocaleStore.getState(), normalizeLocaleConfig(state));
}

export function setCurrentLocale(locale: AvailableLocales) {
  const state = useLocaleStore.getState();
  const supportedLocaleCodes = state.supportedLocales.map(item => item.locale).filter(Boolean);

  if (
    !supportedLocaleCodes.length ||
    supportedLocaleCodes.includes(locale) ||
    locale === state.defaultLocale
  ) {
    useLocaleStore.setState({ currentLocale: locale });
    return;
  }

  const fallbackLocale = (
    state.defaultLocale && supportedLocaleCodes.includes(state.defaultLocale)
      ? state.defaultLocale
      : supportedLocaleCodes[0]
  ) as AvailableLocales;
  useLocaleStore.setState({ currentLocale: fallbackLocale });
}
